import React, { useCallback, useEffect, useState } from 'react';
import { AuditRow, listAudits } from '../../api/authz';
import { ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import SearchSelect from '../../components/SearchSelect';

/**
 * 权限变更记录 (2026-09-14): 谁在什么时候把哪个角色 / 绑定 / 用户组 / 准入改成了什么.
 * 角色规则是覆盖式保存 (先删后插), 不留痕就查不出"普通用户的令牌权限是被谁改没的";
 * 每行可展开看变更前后的完整快照。
 */
const KIND_ZH: Record<string, string> = {
    role: '角色',
    binding: '角色绑定',
    group: '用户组',
    group_member: '组成员',
    user: '用户准入',
};
const ACTION_ZH: Record<string, string> = {
    create: '新建',
    update: '修改',
    delete: '删除',
};
const ACTION_CLS: Record<string, string> = {
    create: 'bg-emerald-50 text-emerald-700',
    update: 'bg-sky-50 text-sky-700',
    delete: 'bg-rose-50 text-rose-600',
};
const PAGE_SIZE = 20;

/** 快照 JSON → 可读文本; 解析失败按原文展示. */
function pretty(raw: string): string {
    if (!raw) return '—';
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
}

function AuditList() {
    const [list, setList] = useState<AuditRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [kind, setKind] = useState('');
    const [actor, setActor] = useState('');
    const [keyword, setKeyword] = useState('');
    const [expanded, setExpanded] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listAudits({
                targetKind: kind || undefined,
                actor: actor || undefined,
                targetId: keyword || undefined,
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.total || 0);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [kind, actor, keyword, page]);

    useEffect(() => {
        load();
    }, [load]);

    const totalPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-4">
            <div className="text-xs leading-relaxed text-slate-500">
                角色、绑定、用户组、准入的每一次改动都会留痕。角色与用户组的权限是覆盖式保存,
                这里能看到改动前后的完整快照。
            </div>

            <div className="flex flex-wrap items-end gap-2">
                <div>
                    <span className="mb-1 block text-xs text-slate-500">
                        变更类型
                    </span>
                    <SearchSelect
                        className="w-36"
                        value={kind}
                        onChange={(v) => {
                            setPage(1);
                            setKind(v);
                        }}
                        placeholder="全部"
                        options={Object.entries(KIND_ZH).map(([k, v]) => ({
                            value: k,
                            label: v,
                        }))}
                        clearable
                    />
                </div>
                <label className="block">
                    <span className="mb-1 block text-xs text-slate-500">
                        操作人邮箱
                    </span>
                    <input
                        className="inp w-56"
                        placeholder="模糊匹配"
                        value={actor}
                        onChange={(e) => setActor(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') setPage(1);
                        }}
                    />
                </label>
                <label className="block">
                    <span className="mb-1 block text-xs text-slate-500">
                        目标 (角色名 / 邮箱 / 组名)
                    </span>
                    <input
                        className="inp w-56"
                        placeholder="精确匹配"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') setPage(1);
                        }}
                    />
                </label>
                <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                        setPage(1);
                        load();
                    }}
                >
                    查询
                </button>
            </div>

            {error && (
                <div className="card border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                    {error}
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-44">时间</th>
                            <th className="th w-44">操作人</th>
                            <th className="th w-24">类型</th>
                            <th className="th">变更</th>
                            <th className="th w-20" />
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={5}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={5}
                                >
                                    暂无变更记录
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            list.map((row) => (
                                <React.Fragment key={row.id}>
                                    <tr className="hover:bg-slate-50/60">
                                        <td className="td text-xs text-slate-500">
                                            {formatTime(row.createdAt)}
                                        </td>
                                        <td className="td">
                                            <div className="text-slate-800">
                                                {row.actorName || '—'}
                                            </div>
                                            <div className="font-mono text-xs text-slate-400">
                                                {row.actorEmail ||
                                                    `uid ${row.actorUid}`}
                                            </div>
                                        </td>
                                        <td className="td text-xs">
                                            <span
                                                className={`tag ${ACTION_CLS[row.action] || 'bg-slate-100 text-slate-600'}`}
                                            >
                                                {ACTION_ZH[row.action] ||
                                                    row.action}
                                            </span>
                                            <div className="mt-0.5 text-slate-500">
                                                {KIND_ZH[row.targetKind] ||
                                                    row.targetKind}
                                            </div>
                                        </td>
                                        <td className="td">
                                            <div className="text-slate-700">
                                                {row.summary}
                                            </div>
                                            <div className="font-mono text-xs text-slate-400">
                                                {row.targetId}
                                            </div>
                                        </td>
                                        <td className="td">
                                            {(row.before || row.after) && (
                                                <button
                                                    type="button"
                                                    className="btn-ghost"
                                                    onClick={() =>
                                                        setExpanded(
                                                            expanded === row.id
                                                                ? null
                                                                : row.id,
                                                        )
                                                    }
                                                >
                                                    {expanded === row.id
                                                        ? '收起'
                                                        : '详情'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {expanded === row.id && (
                                        <tr>
                                            <td
                                                className="td bg-slate-50/60"
                                                colSpan={5}
                                            >
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <div className="mb-1 text-xs text-slate-500">
                                                            变更前
                                                        </div>
                                                        <pre className="max-h-64 overflow-auto rounded border border-slate-200 bg-white p-2 text-xs text-slate-600">
                                                            {pretty(row.before)}
                                                        </pre>
                                                    </div>
                                                    <div>
                                                        <div className="mb-1 text-xs text-slate-500">
                                                            变更后
                                                        </div>
                                                        <pre className="max-h-64 overflow-auto rounded border border-slate-200 bg-white p-2 text-xs text-slate-600">
                                                            {pretty(row.after)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        上一页
                    </button>
                    <span>
                        {page} / {totalPage}
                    </span>
                    <button
                        type="button"
                        className="btn-ghost"
                        disabled={page >= totalPage}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        下一页
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AuditList;
